import React, { useState, useEffect } from 'react';
import { Table, Select, DatePicker, Button, Modal, Form, InputNumber, message, Card, Row, Col } from 'antd';
import { getEmployees, getEmployeeAssignments, getEmployeeTimesheets, createEmployeeTimesheets, updateEmployeeTimesheet } from '../api';
import moment from 'moment';

const { Option } = Select;

const EmployeeTimesheetManager = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timesheetModalVisible, setTimesheetModalVisible] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [timesheetData, setTimesheetData] = useState([]);
  const [saving, setSaving] = useState(false);

  // Fetch all employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await getEmployees();
        if (res.success) {
          setEmployees(res.data);
        }
      } catch (error) {
        message.error('Failed to fetch employees');
      }
    };
    fetchEmployees();
  }, []);

  // Fetch assignments when employee or month changes
  useEffect(() => {
    if (selectedEmployee) {
      fetchAssignments();
    } else {
      setAssignments([]);
    }
  }, [selectedEmployee, selectedMonth]);

  const fetchAssignments = async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    try {
      // Get the first and last day of the selected month
      const startDate = moment(selectedMonth).startOf('month').format('YYYY-MM-DD');
      const endDate = moment(selectedMonth).endOf('month').format('YYYY-MM-DD');
      
      const res = await getEmployeeAssignments({
        employee_id: selectedEmployee, // Ensure this is being passed correctly
        start_date: startDate,
        end_date: endDate,
        with_details: true
      });
      
      if (res.success) {
        // Filter assignments to only include those for the selected employee
        const filteredAssignments = res.data.filter(assignment => 
          assignment.employee_id == selectedEmployee || 
          assignment.employee_details?.id == selectedEmployee
        );
        
        setAssignments(filteredAssignments);
      }
    } catch (error) {
      message.error('Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (value) => {
    // Convert to number to ensure type consistency
    const employeeId = Number(value);
    setSelectedEmployee(employeeId);
    setAssignments([]);
  };

  const handleMonthChange = (date) => {
    setSelectedMonth(date);
  };

  const handleOpenTimesheet = async (assignment) => {
    setSelectedAssignment(assignment);
    
    // Use the assignment's start and end dates
    const startDate = moment(assignment.start_date);
    const endDate = moment(assignment.end_date || assignment.start_date); // Use start_date as fallback if end_date is null
    
    // Generate days for the assignment period
    const daysInRange = [];
    const currentDate = startDate.clone();
    
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      daysInRange.push({
        date: currentDate.format('YYYY-MM-DD'),
        day: currentDate.format('DD'),
        dayOfWeek: currentDate.format('ddd'),
        hours_worked: '',
        id: null
      });
      currentDate.add(1, 'day');
    }

    try {
      // Fetch existing timesheet data for the assignment period
      const res = await getEmployeeTimesheets({
        employee_assignment_id: assignment.id,
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD')
      });

      if (res.success && res.data && res.data.length > 0) {
        // Map existing timesheet data to our days array
        const updatedDays = daysInRange.map(day => {
          const existingEntry = res.data.find(entry => entry.date === day.date);
          return existingEntry 
            ? { ...day, hours_worked: existingEntry.hours_worked, id: existingEntry.id }
            : day;
        });
        
        setTimesheetData(updatedDays);
      } else {
        setTimesheetData(daysInRange);
      }
    } catch (error) {
      console.error('Error loading timesheet data:', error);
      message.error('Failed to load timesheet data');
      setTimesheetData(daysInRange);
    }
    
    setTimesheetModalVisible(true);
  };

  const handleTimesheetChange = (date, value) => {
    const updatedData = timesheetData.map(day => 
      day.date === date ? { ...day, hours_worked: value } : day
    );
    setTimesheetData(updatedData);
  };

  const handleSaveTimesheet = async () => {
    if (!selectedAssignment) return;
    
    setSaving(true);
    try {
      const updates = [];
      
      // Process each day's data
      for (const day of timesheetData) {
        if (day.hours_worked) {
          if (day.id) {
            // Update existing timesheet entry
            updates.push(
              updateEmployeeTimesheet(day.id, {
                hours_worked: day.hours_worked,
                remarks: ''
              })
            );
          } else {
            // Create new timesheet entry
            updates.push(
              createEmployeeTimesheets({
                employee_assignment_id: selectedAssignment.id,
                date: day.date,
                hours_worked: day.hours_worked,
                remarks: ''
              })
            );
          }
        }
      }
      
      await Promise.all(updates);
      message.success('Timesheet saved successfully');
      setTimesheetModalVisible(false);
      fetchAssignments(); // Refresh assignments to show updated data
    } catch (error) {
      message.error('Failed to save timesheet');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Client',
      dataIndex: ['client', 'name'],
      key: 'client_name',
    },
    {
      title: 'Department',
      dataIndex: ['client_department', 'name'],
      key: 'department_name',
    },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      render: (date) => date ? moment(date).format('DD MMM YYYY') : '-',
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      render: (date) => date ? moment(date).format('DD MMM YYYY') : 'Ongoing',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type="link" onClick={() => handleOpenTimesheet(record)}>
          Edit Timesheet
        </Button>
      ),
    },
  ];

  return (
    <div className="container">
      <h2>Employee Timesheet Manager</h2>
      
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} md={12} lg={8} style={{ marginBottom: 16 }}>
            <div>
              <div style={{ marginBottom: 8, fontWeight: '500' }}>Select Employee</div>
              <Select
                style={{ width: '100%' }}
                placeholder="Select Employee"
                onChange={handleEmployeeChange}
                value={selectedEmployee || undefined} // Use undefined instead of null for controlled component
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value={null}>Select an employee</Option>
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {`${emp.emp_code} - ${emp.name}`}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} md={12} lg={8}>
            <div>
              <div style={{ marginBottom: 8, fontWeight: '500' }}>Select Month</div>
              <DatePicker
                style={{ width: '100%' }}
                picker="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                disabled={!selectedEmployee}
              />
            </div>
          </Col>
        </Row>
      </Card>

      {selectedEmployee && (
        <Card title="Assignments">
          <Table
            columns={columns}
            dataSource={assignments}
            rowKey="id"
            loading={loading}
            pagination={false}
          />
        </Card>
      )}

      <Modal
        title={`Timesheet - ${selectedAssignment?.client?.name || ''} (${selectedAssignment?.start_date ? moment(selectedAssignment.start_date).format('DD MMM YYYY') : ''} - ${selectedAssignment?.end_date ? moment(selectedAssignment.end_date).format('DD MMM YYYY') : 'Present'})`}
        visible={timesheetModalVisible}
        onCancel={() => setTimesheetModalVisible(false)}
        width={1000}
        footer={[
          <Button key="cancel" onClick={() => setTimesheetModalVisible(false)}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSaveTimesheet}
            loading={saving}
          >
            Save Timesheet
          </Button>,
        ]}
      >
        {selectedAssignment && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>Date</th>
                  <th style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>Day</th>
                  <th style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>Hours Worked</th>
                </tr>
              </thead>
              <tbody>
                {timesheetData.map((day, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
                      {day.date}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
                      {day.dayOfWeek}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #f0f0f0', textAlign: 'center' }}>
                      <InputNumber
                        min={0}
                        max={24}
                        step={0.5}
                        value={day.hours_worked}
                        onChange={(value) => handleTimesheetChange(day.date, value)}
                        style={{ width: '100%' }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmployeeTimesheetManager;
